import {
  LAND_GROUPING_SECTION_DESCRIPTIONS,
  withDescriptions,
} from './sectionDescriptions'
import { calc, round0, round1, type SectionSpec } from './sectionFields'
import { LAND_SECTION_SPECS } from './landSectionFields'

/**
 * A land grouping is several parcels analysed as one. Its share is written by
 * the SAME fetchers as a single land analysis — verified against production:
 * `noise`, `geo`, `zoning_data`, `buildings` and the nested
 * `purchase_market.purchase` are byte-identical to the land shapes. So every
 * environmental, market and zoning section is reused from `LAND_SECTION_SPECS`
 * rather than redeclared, and the two cannot drift.
 *
 * Only `summary` differs, and it differs a lot: a grouping summary is an
 * aggregate over the group (total areas, parcel numbers, building counts) and
 * carries listings, transactions and construction permits INSIDE itself rather
 * than as sections of their own — the grouping vocabulary has no separate ids
 * for them. Those are emitted here, gated on `summary` like everything else.
 */

/** Section ids a grouping share never writes, so their specs are dropped. */
const NOT_IN_GROUPING = new Set([
  // The grouping declares its own aggregate summary below.
  'summary',
  'purchase_listings',
  'rental_listings',
  'transactions',
  'construction_permits',
  'taxes',
  'electricity',
  'access',
])

const LAND_GROUPING_SPECS: SectionSpec[] = [
  {
    id: 'summary',
    title: 'The grouping',
    fields: [
      calc('total_buildings', 'building_count', 'count'),
      calc('dwellings_nb', 'dwellings_nb', 'count'),
      {
        path: 'building_total_area',
        key: 'building_total_area_m2',
        unit: 'm2',
        source: 'spatial_analysis',
        transform: round1,
      },
      calc('max_floor_nb', 'max_floor_nb', 'count'),
      {
        path: 'max_height',
        key: 'max_height',
        unit: 'm',
        source: 'regulation',
        transform: round1,
      },
      // Ownership is described by shape only. `summary.owners` carries named
      // natural persons ("type": "private"), which never crosses.
      {
        path: 'ownership_type',
        key: 'ownership_type',
        source: 'registre_foncier',
      },
      {
        path: 'owners',
        key: 'owner_count',
        unit: 'count',
        source: 'registre_foncier',
        kind: 'length',
      },
      // Listing, transaction and permit activity on the grouped parcels. These
      // are leaner than the land equivalents — a different shape, not the same
      // one — so they are declared here rather than reused.
      {
        path: 'listings',
        key: 'listings',
        source: 'spatial_analysis',
        kind: 'series',
        item: [
          {
            path: 'listing_date',
            key: 'listed_on',
            source: 'spatial_analysis',
          },
          { path: 'deal_type', key: 'deal_type', source: 'spatial_analysis' },
          { path: 'status', key: 'status', source: 'spatial_analysis' },
          {
            path: 'price',
            key: 'price',
            // A grouping's listings array mixes sales and lets, so the unit
            // comes from each row's own deal_type: a 735 on a "rent" row is a
            // monthly rent, not a sale price.
            unit: 'CHF',
            unit_by: {
              path: 'deal_type',
              map: { purchase: 'CHF', rent: 'CHF/month' },
            },
            source: 'spatial_analysis',
            transform: (v: number) => (v > 0 ? round0(v) : null),
          },
          {
            path: 'area',
            key: 'area_m2',
            unit: 'm2',
            source: 'spatial_analysis',
            transform: (v: number) => (v > 0 ? round1(v) : null),
          },
        ],
      },
      {
        path: 'transactions',
        key: 'transactions',
        source: 'registre_foncier',
        kind: 'series',
        item: [
          { path: 'date', key: 'transaction_date', source: 'registre_foncier' },
          {
            path: 'reason',
            key: 'transaction_type',
            source: 'registre_foncier',
          },
          {
            path: 'price',
            key: 'price_chf',
            unit: 'CHF',
            source: 'registre_foncier',
            // As on a land share: 0 covers both gratuitous transfers and
            // undisclosed prices, so it is reported as unknown, not as free.
            transform: (v: number) => (v > 0 ? round0(v) : null),
          },
        ],
      },
      {
        path: 'construction_permits',
        key: 'construction_permits',
        source: 'geoportal',
        kind: 'series',
        item: [
          { path: 'date', key: 'date', source: 'geoportal' },
          // `regbl_classification_general` is the federal register's own
          // classification. The sibling `classification` field is of ambiguous
          // provenance — it may be the machine-derived one — so it is left out.
          {
            path: 'regbl_classification_general',
            key: 'regbl_classification_general',
            source: 'geoportal',
          },
        ],
      },
    ],
  },
  ...LAND_SECTION_SPECS.filter((s) => !NOT_IN_GROUPING.has(s.id as string)),
]

export const LAND_GROUPING_SECTION_SPECS: SectionSpec[] = withDescriptions(
  LAND_GROUPING_SPECS,
  LAND_GROUPING_SECTION_DESCRIPTIONS
)
